import {
  PushNotifications,
  Token,
  ActionPerformed,
} from "@capacitor/push-notifications";
import { platformService } from "./platform";
import { apiClient } from "../api/client";
import { report } from "@services/Sentry.ts";

// Convert a base64 string to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const pushNotificationService = {
  _initialized: false,

  /**
   * Initialize push notifications based on platform
   */
  async initialize(): Promise<void> {
    if (this._initialized) {
      return;
    }
    this._initialized = true;

    if (platformService.isNative()) {
      await this.initializeNative();
    } else if (platformService.supportsPushNotifications()) {
      console.log("Initializing web push notifications");
      await this.initializeWebPush();
    }
  },

  /**
   * Initialize native push notifications (iOS/Android)
   */
  async initializeNative(): Promise<void> {
    // Request permission
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === "prompt") {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== "granted") {
      console.log("Push notification permission not granted");
      return;
    }

    // Register with APNs/FCM
    await PushNotifications.register();

    // Handle registration success
    PushNotifications.addListener("registration", async (token: Token) => {
      await this.registerWithBackend({
        platform: platformService.getPlatform(),
        fcm_token: token.value,
      });
    });

    // Handle registration error
    PushNotifications.addListener("registrationError", (error) => {
      console.error("Push registration error:", error);
      report(error);
    });

    // Handle notification received while app is in foreground
    PushNotifications.addListener(
      "pushNotificationReceived",
      (notification) => {
        console.log("Push notification received:", notification);
        // You can show an in-app notification here
      }
    );

    // Handle notification action (user tapped on notification)
    PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (action: ActionPerformed) => {
        console.log("Push action performed:", action);
        // Handle navigation based on notification data
        const data = action.notification.data;
        if (data?.url) {
          // Navigate to the URL in the app
          window.location.href = data.url;
        }
      }
    );
  },

  /**
   * Initialize Web Push notifications (PWA)
   */
  async initializeWebPush(): Promise<void> {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.log("Web Push not supported");
      return;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.log("Notification permission not granted");
      return;
    }

    try {
      // Get VAPID public key from backend
      const { data: vapidData } = await apiClient.get<{ public_key: string }>(
        "/push/vapid-key"
      );

      if (!vapidData.public_key) {
        console.log("No VAPID key configured on backend");
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Check for existing subscription
      let subscription = await registration.pushManager.getSubscription();

      // If no subscription exists, create one
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidData.public_key),
        });
      }

      // Register with backend
      const subscriptionJson = subscription.toJSON();
      await this.registerWithBackend({
        platform: "web",
        endpoint: subscription.endpoint,
        p256dh: subscriptionJson.keys?.p256dh || "",
        auth_key: subscriptionJson.keys?.auth || "",
      });
    } catch (error) {
      console.error("Failed to initialize web push:", error);
      report(error);
    }
  },

  /**
   * Register push subscription with backend
   */
  async registerWithBackend(data: {
    platform: string;
    fcm_token?: string;
    endpoint?: string;
    p256dh?: string;
    auth_key?: string;
  }): Promise<void> {
    try {
      await apiClient.post("/push/register", data);
      console.log("Push subscription registered with backend");
    } catch (error) {
      console.error("Failed to register push subscription:", error);
      report(error);
    }
  },

  /**
   * Unregister push subscription
   */
  async unregister(): Promise<void> {
    this._initialized = false;
    try {
      if (platformService.isNative()) {
        await PushNotifications.unregister();
        // Note: We don't have the token here to send to backend
        // The backend will clean up on logout
      } else if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
          await apiClient.delete("/push/unregister", {
            data: {
              platform: "web",
              endpoint: subscription.endpoint,
            },
          });
          await subscription.unsubscribe();
        }
      }
    } catch (error) {
      console.error("Failed to unregister push:", error);
      report(error);
    }
  },

  /**
   * Check if push notifications are enabled
   */
  async isEnabled(): Promise<boolean> {
    if (platformService.isNative()) {
      const status = await PushNotifications.checkPermissions();
      return status.receive === "granted";
    } else if ("Notification" in window) {
      return Notification.permission === "granted";
    }
    return false;
  },
};
